import React, { useMemo } from 'react';
import './FlowSequencePreview.css';

const FlowSequencePreview = ({ nodes, edges }) => {
    const sequence = useMemo(() => {
        if (!nodes || !edges || nodes.length === 0) {
            return [];
        }

        // Find the trigger node (start of sequence)
        const triggerNode = nodes.find(node => node.type === 'trigger');
        if (!triggerNode) return [];

        // Build sequence by following edges
        const buildSequence = (currentNodeId, visited = new Set()) => {
            if (visited.has(currentNodeId)) return []; // Prevent infinite loops
            visited.add(currentNodeId);

            const currentNode = nodes.find(n => n.id === currentNodeId);
            if (!currentNode) return [];

            const sequence = [];
            
            // Add current node to sequence
            if (currentNode.type !== 'trigger') {
                sequence.push({
                    id: currentNode.id,
                    type: currentNode.type,
                    data: currentNode.data,
                    node: currentNode
                });
            }

            // Find next nodes
            const outgoingEdges = edges.filter(edge => edge.source === currentNodeId);
            for (const edge of outgoingEdges) {
                sequence.push(...buildSequence(edge.target, new Set(visited)));
            }

            return sequence;
        };

        return buildSequence(triggerNode.id);
    }, [nodes, edges]);

    const formatDelay = (delayAmount, delayType) => {
        if (!delayAmount || !delayType) return 'immediately';
        
        const unit = delayAmount === 1 ? delayType.slice(0, -1) : delayType;
        return `${delayAmount} ${unit}`;
    };

    const calculateTotalDuration = () => {
        let totalMinutes = 0;
        
        sequence.forEach(item => {
            if (item.type === 'delay') {
                const amount = item.data.delayAmount || 0;
                const type = item.data.delayType || 'minutes';
                
                switch (type) {
                    case 'minutes':
                        totalMinutes += amount;
                        break;
                    case 'hours':
                        totalMinutes += amount * 60;
                        break;
                    case 'days':
                        totalMinutes += amount * 24 * 60;
                        break;
                    case 'weeks':
                        totalMinutes += amount * 7 * 24 * 60;
                        break;
                }
            }
        });

        if (totalMinutes === 0) return 'Immediate';
        if (totalMinutes < 60) return `${totalMinutes} minutes`;
        if (totalMinutes < 1440) return `${Math.round(totalMinutes / 60)} hours`;
        return `${Math.round(totalMinutes / 1440)} days`;
    };

    const getEmailCount = () => {
        return sequence.filter(item => item.type === 'email').length;
    };

    if (sequence.length === 0) {
        return (
            <div className="flow-sequence-preview">
                <div className="sequence-header">
                    <h4>📧 Email Sequence Preview</h4>
                    <div className="sequence-stats">
                        <span className="stat-item">0 emails</span>
                        <span className="stat-item">No duration</span>
                    </div>
                </div>
                <div className="empty-sequence">
                    <span className="empty-icon">📭</span>
                    <p>Add email nodes to see sequence preview</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flow-sequence-preview">
            <div className="sequence-header">
                <h4>📧 Email Sequence Preview</h4>
                <div className="sequence-stats">
                    <span className="stat-item">{getEmailCount()} emails</span>
                    <span className="stat-item">{calculateTotalDuration()}</span>
                </div>
            </div>

            <div className="sequence-timeline">
                <div className="timeline-start">
                    <div className="timeline-node start">
                        <span className="timeline-icon">🚀</span>
                    </div>
                    <div className="timeline-label">Flow starts</div>
                </div>

                {sequence.map((item, index) => (
                    <div key={item.id} className="timeline-item">
                        <div className="timeline-connector"></div>
                        
                        {item.type === 'email' && (
                            <div className="timeline-node email">
                                <span className="timeline-icon">📧</span>
                                <div className="timeline-content">
                                    <div className="email-preview">
                                        <h5>{item.data.label || 'Email'}</h5>
                                        <p className="email-subject">
                                            <strong>Subject:</strong> {item.data.subject || 'No subject'}
                                        </p>
                                        {item.data.templateId && (
                                            <div className="email-template">
                                                <span className="template-badge">✅ Template Set</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {item.type === 'delay' && (
                            <div className="timeline-node delay">
                                <span className="timeline-icon">⏰</span>
                                <div className="timeline-content">
                                    <div className="delay-preview">
                                        <h5>Wait {formatDelay(item.data.delayAmount, item.data.delayType)}</h5>
                                        <p>Delay before next email</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {item.type === 'condition' && (
                            <div className="timeline-node condition">
                                <span className="timeline-icon">❓</span>
                                <div className="timeline-content">
                                    <div className="condition-preview">
                                        <h5>{item.data.label || 'Condition'}</h5>
                                        <p>Branches based on customer behavior</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ))}

                <div className="timeline-end">
                    <div className="timeline-connector"></div>
                    <div className="timeline-node end">
                        <span className="timeline-icon">🎯</span>
                    </div>
                    <div className="timeline-label">Flow complete</div>
                </div>
            </div>

            <div className="sequence-summary">
                <div className="summary-card">
                    <span className="summary-icon">📊</span>
                    <div className="summary-content">
                        <h6>Flow Statistics</h6>
                        <div className="summary-stats">
                            <span>{getEmailCount()} emails will be sent</span>
                            <span>Over {calculateTotalDuration().toLowerCase()}</span>
                            <span>{sequence.filter(s => s.type === 'delay').length} delays configured</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FlowSequencePreview;
